// Localização: ./frontend/src/pages/LoginPage.js - Versão 3.0.0

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Link, useNavigate, useLocation } from 'react-router-dom';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [senha, setSenha] = useState('');
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [loading, setLoading] = useState(false);
    
    const { handleLogin } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    // Obter a página de origem do state, se existir
    const from = location.state?.from || '/dashboard';
    // Obter mensagem de sucesso e credenciais do state (vindo do registro)
    const successMessage = location.state?.message;
    const emailFromState = location.state?.email;
    const senhaFromState = location.state?.senha;
    
    // Exibir mensagem de sucesso e pré-preencher campos se vier do registro
    useEffect(() => {
        if (successMessage) {
            setSuccess(successMessage);
            // Limpar mensagem após 5 segundos
            const timer = setTimeout(() => {
                setSuccess(null);
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [successMessage]);

    // Pré-preencher email e senha se vier do registro (apenas uma vez quando vier do registro)
    useEffect(() => {
        if (emailFromState) {
            setEmail(emailFromState);
        }
        if (senhaFromState) {
            setSenha(senhaFromState);
        }
        // Nota: O React Router limpa o state automaticamente quando a navegação muda
        // Não precisamos limpar manualmente, e o state é apenas em memória (não exposto na URL)
    }, [emailFromState, senhaFromState]); // Executar quando os valores do state chegarem

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setLoading(true);

        try {
            await handleLogin(email, senha);
            // Se o login for bem-sucedido, redireciona para a página de origem ou dashboard
            // O PrivateRoute vai verificar a assinatura e redirecionar para SemAssinaturaPage se necessário
            navigate(from, { replace: true });
        } catch (err) {
            // O handleLogin já deve lidar com o erro e retornar uma exceção ou mensagem
            setError(err.message || 'Falha ao conectar. Verifique suas credenciais.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-fundo-cinza-moderno">
            <div className="w-full max-w-md p-10 space-y-8 bg-white rounded-2xl shadow-soft animate-[fadeIn_0.5s_ease-out_forwards]">
                
                {/* Cabeçalho */}
                <div className="text-center space-y-4">
                    <div className="flex justify-center">
                        <img 
                            src="/images/higeia-icon-only.png"
                            alt="Higeia.tech Logo"
                            className="h-16 w-16 object-contain transition-transform duration-300 hover:scale-110"
                        />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-3xl font-extrabold text-gray-900 tracking-tight">
                            Higeia.tech
                        </h2>
                        <p className="text-sm text-gray-500 font-medium">
                            Acesso ao Painel
                        </p>
                    </div>
                </div>

                {/* Formulário */}
                <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
                    
                    {success && (
                        <div 
                            className="p-3 text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg transition-all duration-300 ease-out animate-[slideInFromTop_0.3s_ease-out]" 
                            role="alert"
                        >
                            {success}
                        </div>
                    )}

                    {error && (
                        <div 
                            className="p-3 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg transition-all duration-300 ease-out animate-[slideInFromTop_0.3s_ease-out]" 
                            role="alert"
                        >
                            {error}
                        </div>
                    )}

                    <div className="space-y-1">
                        <label htmlFor="email" className="sr-only">Email</label>
                        <input
                            id="email"
                            name="email"
                            type="email"
                            required
                            className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-200 placeholder-gray-400 text-gray-900 
                                       bg-white transition-all duration-300 ease-in-out
                                       focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300
                                       hover:border-gray-300"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div className="space-y-1">
                        <label htmlFor="senha" className="sr-only">Senha</label>
                        <input
                            id="senha"
                            name="senha"
                            type="password"
                            required
                            className="appearance-none rounded-lg relative block w-full px-4 py-3 border border-gray-200 placeholder-gray-400 text-gray-900 
                                       bg-white transition-all duration-300 ease-in-out
                                       focus:outline-none focus:border-gray-400 focus:ring-1 focus:ring-gray-300
                                       hover:border-gray-300"
                            placeholder="Senha"
                            value={senha}
                            onChange={(e) => setSenha(e.target.value)}
                        />
                    </div>

                    <button
                        type="submit"
                        className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg 
                                  text-sm font-semibold text-white transition-all duration-300 ease-in-out
                                  ${loading 
                                    ? 'bg-gray-400 cursor-not-allowed' 
                                    : 'bg-higeia-medium hover:bg-higeia-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-300 shadow-sm hover:shadow-md transform hover:-translate-y-0.5'
                                  }`}
                        disabled={loading}
                    >
                        {loading ? (
                            <span className="flex items-center">
                                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                </svg>
                                Entrando...
                            </span>
                        ) : (
                            'Entrar'
                        )}
                    </button>
                </form>

                {/* Rodapé do Formulário */}
                <div className="text-center text-sm pt-4 border-t border-gray-100">
                    <div>
                        <Link 
                            to="/forgot-password" 
                            className="text-sm text-gray-600 hover:text-higeia-medium transition-colors duration-200 underline-offset-2 hover:underline"
                        >
                            Esqueci minha senha
                        </Link>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default LoginPage;